import React, { useState, useRef, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { Skeleton } from 'antd';
import { withTranslation } from 'react-i18next';
import moment from 'moment';

import { JOB_QUEUE_MODAL as trans } from '../TranslationValues';
import { dummyCallback } from '../../../../utils/complexDocumentConstants';
import ReusableModal from '../../../../components/ReusableComponents/ReusableModal';
import { JobQueueContainer, CardTitle, Label, BatchStatus, Card, CardWrapper } from './styles';
import { getChannelTotals, isEmptyObject, aggregatedData, isShortMessageEnabled } from '../../../../utils/helper';
import SearchableTable from './SearchableTable';
import { batchInitiation, metaSearchKeyMappings } from '../../../../utils/constants/statusMonitoring';
import { getJobStats } from '../../../../utils/services/statusMonitoringAPI';

const namespaces = ['JOB_QUEUE_MODAL', 'STATUS_MONITORING', 'SEARCHABLE_TABLE'];

const jobChannelLabels = ['print', 'email', 'messages'];
const jobStatsPayload = {status: 'yet_to_start', stage: 'inprogress'}

const JOBQueueModal = ({
  visible = false,
  onCancel = dummyCallback,
  title = 'Modal Title',
  t,
  activeItem,
  users,
  jobId,
  contentType
}) => {
  const [statsFilter, setStatsFilter] = useState([]);
  const [jobStats, setJobStats] = useState({print:0 , email:0, messages:0});
  const chartRef = useRef(null);
  const keyNames = metaSearchKeyMappings(t);

  const batchType = activeItem?.batchType;
  const isHybrid = batchType === 'HYBRID';
  const chartLabel = Object.keys(getChannelTotals(activeItem?.batchStats || [], true, t));  

  const chartData = {
    series: !isShortMessageEnabled ? [jobStats.print, jobStats.email] : [jobStats.print, jobStats.email, jobStats.messages],
    options: {
      labels: chartLabel.filter(item => item !== 'Hybrid'),
      colors: ['#6CE5E8','#FBE83A','#C8E066'],
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: '50%',
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        floating: false,
        offsetY: 0,
        offsetX: 0,
        formatter: function(val, opts) {
          return (
            '<span style="font-family: Lato">' +
            val +
            ': ' +
            opts.w.globals.series[opts.seriesIndex] +
            '</span>'
          );
        },
      },
      tooltip: {
        enabled: false,
      },
      chart: {
        events: {
          dataPointSelection: function(event, chartContext, opts) {
            let selectedChannel = [];
            const labels = opts.w.config.labels
            opts.selectedDataPoints[0].forEach(dataPoint => {
              selectedChannel.push({key: keyNames.channel, value:labels[dataPoint]});
             });
             setStatsFilter(selectedChannel);
          },
        },
      },
      states: {
        active: {
            allowMultipleDataPointsSelection: true,
            filter: {
              type: 'darken'
            }
        },
      }
    },
  };

  const userDetail = users.find(list => list.id === Number(activeItem?.triggeredBy));
  const recordDetails = aggregatedData(activeItem?.batchStats || []);
  const loading = true;

  const unHighlightSeries =(value) =>{
    if(value === jobChannelLabels[0]){
      value = 'Documents'
    }
    value = value.charAt(0).toUpperCase() + value.slice(1);
    chartRef.current.chart.toggleSeries(value);
  }

  useEffect(() => {
    if (visible) {
      jobStatsPayload.jobId = jobId
      const channelTotals = getChannelTotals(activeItem?.batchStats || [], false, t)
      setJobStats(channelTotals)
    }
  }, [visible, jobId]);
  
  return (
    <ReusableModal
      visible={visible}
      title={title}
      onCancel={() => onCancel(false)}
      width="90%"
      styleProps={{
        top: 'auto',
        margin: '0 auto',
      }}
      bodyStyleProps={{
        padding: '20px',
        overflow: 'auto',
      }}
      footer={null}
      centered
      maskClosable={true}
      className="job-queue right-aligned-modal"
      transitionName={'ant-modal-slide-in'}
    >
      <JobQueueContainer>
        {!isHybrid && (
          <>
            {/* stats card */}
            <div className="leftContainer job_queue_info">
              <div className="stats_container">
                <CardWrapper>
                  <CardTitle textAlign={'center'}>
                    {t(trans.INITIATED_BY_LABEL.key, trans.INITIATED_BY_LABEL.value)}
                  </CardTitle>
                  <Card className="card">
                    {isEmptyObject(activeItem) === loading ? (
                      <Skeleton active />
                    ) : (
                      <>
                        {userDetail?.displayName && (
                          <>
                            <Label color="black" fontWeight={700}>
                              {t(trans.INITIATED_BY_LABEL.key, trans.INITIATED_BY_LABEL.value)}
                            </Label>
                            <Label>{userDetail?.displayName} </Label>{' '}
                          </>
                        )}
                        {activeItem?.initiatedFrom && (
                          <>
                            <Label color="black" fontWeight={700}>
                              {t(trans.INITIATED_FROM_LABEL.key, trans.INITIATED_FROM_LABEL.value)}
                            </Label>
                            <Label>
                              {batchInitiation(t).find(
                                item => item.key === activeItem?.initiatedFrom,
                              )?.value || ''}
                            </Label>
                          </>
                        )}
                        <Label color="black" fontWeight={700}>
                          {t(trans.INITIATED_ON_LABEL.key, trans.INITIATED_ON_LABEL.value)}
                        </Label>
                        <Label>
                          {moment(activeItem?.createdTime).format('D, MMMM YYYY')} &nbsp;
                          {moment(activeItem?.createdTime).format('LT')}
                        </Label>
                      </>
                    )}
                  </Card>
                </CardWrapper>

                <CardWrapper>
                  <CardTitle>{t(trans.STATS_TITLE.key, trans.STATS_TITLE.value)} </CardTitle>
                  <Card className="card">
                    {isEmptyObject(activeItem) === loading ? (
                      <Skeleton active />
                    ) : (
                      <Chart
                        type="donut"
                        options={chartData.options}
                        series={chartData.series}
                        width={'100%'}
                        ref={chartRef}
                      />
                    )}
                  </Card>
                </CardWrapper>
              </div>
            </div>

            {/* record details */}
            <div className="middleContainer job_queue_info">
              <CardTitle textAlign={'center'}>
                {t(trans.RECORD_DETAILS_TITLE.key, trans.RECORD_DETAILS_TITLE.value)}
              </CardTitle>
              <Card className="card" overFlowScroll>
                {isEmptyObject(activeItem) === loading ? (
                  <Skeleton active />
                ) : (
                  <>
                    {' '}
                    <BatchStatus>
                      {t(
                        trans.BATCH_RECEPTION_START_STATUS.key,
                        trans.BATCH_RECEPTION_START_STATUS.value,
                      )}
                    </BatchStatus>
                    {recordDetails.map((list, index) => (
                      <div className="batch_list">
                        <div style={{ width: '20%' }}>
                          {t(trans.BATCH_LABEL.key, trans.BATCH_LABEL.value)} {index + 1}
                        </div>
                        <div style={{ width: '40%' }}>
                          <div>
                            {list.totalCount}{' '}
                            {t(trans.RECORDS_LABEL.key, trans.RECORDS_LABEL.value)}
                          </div>
                          <div>{list.createdOn} </div>
                          <div>{list.createdOnTime} </div>
                        </div>
                        <div style={{ width: '30%' }}>
                          <span style={{ fontWeight: '700', color: 'black' }}>
                            {list.failureCount + list.successCount} / {list.totalCount} &nbsp;
                          </span>
                          {t(trans.PROCESSING_LABEL.key, trans.PROCESSING_LABEL.value)}
                        </div>
                      </div>
                    ))}
                    <BatchStatus marginTop={'25px'}>
                      {activeItem?.batchEnd &&
                        t(
                          trans.BATCH_RECEPTION_COMPLETED_STATUS.key,
                          trans.BATCH_RECEPTION_COMPLETED_STATUS.value,
                        )}
                    </BatchStatus>
                  </>
                )}
              </Card>
            </div>
          </>
        )}
        {/* table  */}
        <div className="rightcontainer job_queue_table" style={{width: isHybrid && '100%'}}>
          <CardTitle textAlign={'center'}>
            {t(trans.RECORD_SEARCH_TITLE.key, trans.RECORD_SEARCH_TITLE.value)}
          </CardTitle>
          <Card className="card">
            {isEmptyObject(activeItem) === loading ? (
              <Skeleton active />
            ) : (
              <>
                <SearchableTable
                  jobId={jobId}
                  visible={visible}
                  contentType={contentType}
                  defaultColumnCount={2}
                  statsFilter={statsFilter}
                  setStatsFilter={setStatsFilter}
                  statsKeys={[keyNames.channel]}
                  activeChannel={isHybrid ? 'Hybrid' : undefined}
                  chartCallBack={unHighlightSeries}
                  staticStatsKeys={isHybrid ? ['address']:['uniq_id', 'uniq_name', 'channel', 'language']}
                />
              </>
            )}
          </Card>
        </div>
      </JobQueueContainer>
    </ReusableModal>
  );
};

export default withTranslation(namespaces)(JOBQueueModal);
